'use strict';

/**
 * Bootstrap marketing modal after shell loads. Loads CSS/JS from bootstrap `assets` before injecting
 * HTML. Skips bootstrap API for 2h after modal display or dismiss (localStorage).
 */

const STORAGE_PREFIX = 'rpm_promotion_modal_';

/** @type {number} Skip bootstrap API until this many ms after modal shown or dismissed */
const THROTTLE_MS = 2 * 60 * 60 * 1000;

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function toModalId(value) {
	if (typeof value !== 'number' && typeof value !== 'string') {
		return null;
	}
	const normalized = typeof value === 'string' ? value.trim() : value;
	if (normalized === '') {
		return null;
	}
	const parsed = Number(normalized);
	const isValid = Number.isInteger(parsed) && parsed >= 0
		&& (typeof value === 'number' || String(parsed) === normalized);
	return isValid ? parsed : null;
}

/**
 * @param {unknown} parsed
 * @returns {number[]}
 */
function parseIds(parsed) {
	if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.ids)) {
		return [];
	}
	return parsed.ids.map((id) => toModalId(id)).filter((id) => id !== null);
}

/**
 * @param {string} key
 * @returns {{ ids: number[], throttle_until: number|null }}
 */
function readState(key) {
	try {
		const raw = globalThis.localStorage.getItem(key);
		if (!raw) {
			return { ids: [], throttle_until: null };
		}
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') {
			return { ids: [], throttle_until: null };
		}
		const throttleRaw = parsed.throttle_until;
		const throttleUntil = typeof throttleRaw === 'number' && Number.isFinite(throttleRaw)
			? throttleRaw
			: null;

		return {
			ids: parseIds(parsed),
			throttle_until: throttleUntil,
		};
	} catch (e) {
		return { ids: [], throttle_until: null };
	}
}

/**
 * @param {string} key
 * @param {{ ids: number[], throttle_until?: number|null }} state
 */
function writeState(key, state) {
	const payload = { ids: state.ids };
	if (typeof state.throttle_until === 'number' && Number.isFinite(state.throttle_until)) {
		payload.throttle_until = state.throttle_until;
	}
	try {
		globalThis.localStorage.setItem(key, JSON.stringify(payload));
	} catch (e) {
		/* ignore quota */
	}
}

/**
 * @param {{ throttle_until: number|null }} state
 * @returns {boolean}
 */
function shouldSkipBootstrap(state) {
	return typeof state.throttle_until === 'number'
		&& Number.isFinite(state.throttle_until)
		&& state.throttle_until > Date.now();
}

/**
 * @param {string} key
 */
function setThrottle(key) {
	const state = readState(key);
	state.throttle_until = Date.now() + THROTTLE_MS;
	writeState(key, state);
}

/**
 * @param {string} key
 * @param {number} modalId
 */
function rememberModalId(key, modalId) {
	const id = toModalId(modalId);
	if (id === null) {
		return;
	}
	const state = readState(key);
	if (state.ids.indexOf(id) === -1) {
		state.ids.push(id);
	}
	writeState(key, state);
}

/**
 * @param {string} key
 * @param {{ ids?: unknown[] }} clientState
 * @param {boolean} clearThrottle
 */
function persistClientState(key, clientState, clearThrottle) {
	const state = readState(key);
	if (clientState && Array.isArray(clientState.ids)) {
		state.ids = parseIds(clientState);
	}
	if (clearThrottle) {
		state.throttle_until = null;
	}
	writeState(key, state);
}

/**
 * @param {{ show?: unknown, modal_html?: unknown, modal_id?: unknown }} resp
 * @returns {boolean}
 */
function shouldDisplayPromotionModal(resp) {
	return resp.show === true
		&& typeof resp.modal_html === 'string'
		&& resp.modal_html !== ''
		&& toModalId(resp.modal_id) !== null;
}

/**
 * Decide how the client throttle should change after a bootstrap response.
 * Clearing the throttle on every `show: false` lets a dismissed shopper re-hit the bootstrap API on
 * every page load once the throttle window expires. When the active modal was dismissed by this browser
 * (the server prunes its id into client_state.ids) we re-throttle to keep backing off; we only clear
 * when there is nothing to show or dismiss (e.g. the store has no active modal).
 *
 * @param {{ show?: unknown, modal_html?: unknown, modal_id?: unknown, client_state?: { ids?: unknown[] } }} resp
 * @returns {'keep'|'rethrottle'|'clear'}
 */
function throttleActionForResponse(resp) {
	if (shouldDisplayPromotionModal(resp)) {
		return 'keep';
	}
	const ids = resp.client_state ? parseIds(resp.client_state) : [];
	return ids.length > 0 ? 'rethrottle' : 'clear';
}

/** Storefront CTA backgrounds only (never the header shell — it is often white). */
const STOREFRONT_ACCENT_SELECTORS = [
	'header.container-header .cart-button.call-to-action-button',
	'header.container-header .call-to-action-button',
];

/**
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
function isUsableCssColor(value) {
	return typeof value === 'string'
		&& value !== ''
		&& value !== 'transparent'
		&& value !== 'rgba(0, 0, 0, 0)';
}

/**
 * @param {string} rgb
 * @returns {boolean}
 */
function isNeutralAccentColor(rgb) {
	const match = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(rgb);
	if (match === null) {
		return false;
	}
	const channels = [Number(match[1]), Number(match[2]), Number(match[3])];
	const max = Math.max(...channels);
	const min = Math.min(...channels);
	if ((max - min) >= 15) {
		return false;
	}
	// Low-saturation grays: reject dark defaults, white header, and light chrome.
	return max < 90 || max >= 200;
}

/**
 * @param {Element|null|undefined} element
 * @returns {string|null}
 */
function readElementBackgroundColor(element) {
	if (!(element instanceof Element)) {
		return null;
	}
	const backgroundColor = globalThis.getComputedStyle(element).backgroundColor;
	if (!isUsableCssColor(backgroundColor) || isNeutralAccentColor(backgroundColor)) {
		return null;
	}

	return backgroundColor;
}

/**
 * @returns {HTMLElement|null}
 */
function getPromotionModalHost() {
	if (typeof document === 'undefined') {
		return null;
	}

	const host = document.getElementById('rp-promotion-modal-host');
	return host instanceof HTMLElement ? host : null;
}

/**
 * Read the active storefront accent from header chrome (never from inside the modal host).
 *
 * @returns {string|null}
 */
function resolveStorefrontAccentColor() {
	if (typeof document === 'undefined') {
		return null;
	}

	const host = getPromotionModalHost();
	for (let i = 0; i < STOREFRONT_ACCENT_SELECTORS.length; i += 1) {
		const candidate = document.querySelector(STOREFRONT_ACCENT_SELECTORS[i]);
		if (candidate instanceof Element && host !== null && host.contains(candidate)) {
			continue;
		}
		const color = readElementBackgroundColor(candidate);
		if (color !== null) {
			return color;
		}
	}

	return null;
}

/**
 * Copy the header cart / nav accent onto the modal root for SCSS tokens.
 *
 * @param {Element|null|undefined} modalRoot
 */
function applyPromotionModalThemeColors(modalRoot) {
	if (!(modalRoot instanceof HTMLElement)) {
		return;
	}

	const accent = resolveStorefrontAccentColor();
	if (accent !== null) {
		modalRoot.style.setProperty('--rpm-pm-theme-accent', accent);
	}
}

/**
 * @param {string[]} urls
 * @returns {Promise<void>}
 */
function loadStyles(urls) {
	return urls.reduce(
		(chain, href) => chain.then(
			() => new Promise((resolve, reject) => {
				const link = document.createElement('link');
				link.rel = 'stylesheet';
				link.href = href;
				link.onload = () => resolve();
				link.onerror = () => reject(new Error('stylesheet ' + href));
				document.head.appendChild(link);
			}),
		),
		Promise.resolve(),
	);
}

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
	return new Promise((resolve, reject) => {
		const s = document.createElement('script');
		s.src = src;
		s.async = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error('script ' + src));
		document.body.appendChild(s);
	});
}

function run() {
	if (!globalThis.rp_app || !globalThis.rp_app.data || !globalThis.rp_app.data.promotion_modal) {
		return;
	}

	const host = document.getElementById('rp-promotion-modal-host');
	if (!host || typeof globalThis.rp_app.request !== 'function') {
		return;
	}

	const mainEl = document.querySelector('.main');
	const fromMain = mainEl !== null ? mainEl.getAttribute('data-store-hash') : null;
	const storeHash = document.body.getAttribute('data-store-hash') || fromMain || 'default';
	const key = STORAGE_PREFIX + storeHash;
	const state = readState(key);

	if (shouldSkipBootstrap(state)) {
		return;
	}

	globalThis.__rpmOnPromotionModalDismiss = function (modalId) {
		rememberModalId(key, modalId);
		setThrottle(key);
	};

	const strings = globalThis.rp_app.data.promotion_modal;
	const defaultSubmitError = (typeof strings.error_generic === 'string' && strings.error_generic !== '')
		? strings.error_generic
		: 'Request could not be completed.';

	const body = JSON.stringify({ client_state: { ids: state.ids } });

	globalThis.rp_app.request({
		method: 'POST',
		url: '/ajax/promotion-modal/bootstrap',
		contentType: 'application/json; charset=UTF-8',
		dataType: 'json',
		data: body,
		success(resp) {
			if (!resp || typeof resp !== 'object') {
				return;
			}
			if (resp.client_state && typeof resp.client_state === 'object') {
				const throttleAction = throttleActionForResponse(resp);
				persistClientState(key, resp.client_state, throttleAction === 'clear');
				if (throttleAction === 'rethrottle') {
					setThrottle(key);
				}
			}
			if (!shouldDisplayPromotionModal(resp)) {
				return;
			}
			const modalId = toModalId(resp.modal_id);
			const assets = resp.assets && typeof resp.assets === 'object' ? resp.assets : {};
			const cssUrls = Array.isArray(assets.css) ? assets.css : [];
			const jsUrls = Array.isArray(assets.js) ? assets.js : [];
			const needsCss = cssUrls.length > 0;
			const cssPromise = needsCss ? loadStyles(cssUrls) : Promise.resolve();

			cssPromise.then(() => {
				host.innerHTML = resp.modal_html;
				const root = host.querySelector('[data-rpm-pm-root]');
				if (!root) {
					host.innerHTML = '';
					return;
				}

				applyPromotionModalThemeColors(root);

				const successUi = resp.success_ui && typeof resp.success_ui === 'object' ? resp.success_ui : {};

				const onModalShown = () => {
					setThrottle(key);
				};

				const attach = () => {
					if (typeof globalThis.__rpmAttachPromotionModal !== 'function') {
						return false;
					}
					globalThis.__rpmAttachPromotionModal(root, modalId, successUi, defaultSubmitError);
					onModalShown();
					return true;
				};

				const teardownFailedInject = () => {
					host.innerHTML = '';
				};

				if (jsUrls.length === 0) {
					if (!attach()) {
						teardownFailedInject();
					}
					return;
				}

				jsUrls
					.reduce(
						(p, src) => p.then(() => loadScript(src)),
						Promise.resolve(),
					)
					.then(() => {
						if (!attach()) {
							teardownFailedInject();
						}
					})
					.catch(() => {
						teardownFailedInject();
					});
			}).catch(() => {
				if (needsCss) {
					/* Required stylesheet(s) failed; do not show an unstyled modal. */
				}
			});
		},
	});
}

module.exports = run;
module.exports.THROTTLE_MS = THROTTLE_MS;
module.exports.readState = readState;
module.exports.writeState = writeState;
module.exports.shouldSkipBootstrap = shouldSkipBootstrap;
module.exports.setThrottle = setThrottle;
module.exports.rememberModalId = rememberModalId;
module.exports.persistClientState = persistClientState;
module.exports.toModalId = toModalId;
module.exports.shouldDisplayPromotionModal = shouldDisplayPromotionModal;
module.exports.throttleActionForResponse = throttleActionForResponse;
module.exports.applyPromotionModalThemeColors = applyPromotionModalThemeColors;
module.exports.resolveStorefrontAccentColor = resolveStorefrontAccentColor;
